import { ChecklistItemRow } from './list'
import { createPortal } from 'react-dom'
import { match, P } from '@st/pattern'
import { Modal, Dialog, DialogButtons, Button, Spinner, Checklist } from '@st/theme'
import type { STChecklistItem, STDocumentType, STMissingReason } from '@st/sdk'
import type { ChecklistMessage } from './module'
import { type ChecklistDialog, formatChecklistItem } from './model'
import { checklistAttachmentModel, checklistItemDialogModel } from './selectors'
import { DocumentRequestDialog, QuestionnaireEntryDialog } from './intake/dialogs'
import { TaxPaymentDialog } from './delivery/dialog'
import { ChecklistItemDialog } from './item-dialog'
import { ManageChecklistItemCompletion } from './intake/completion-dialog'
export function ChecklistDialogs({
  dialog,
  documentTypes,
  reasons,
  sections,
  send
}: {
  dialog: ChecklistDialog | undefined
  documentTypes: STDocumentType[]
  reasons: Record<string, STMissingReason[]>
  sections: Parameters<typeof checklistAttachmentModel>[0]
  send: (message: ChecklistMessage) => void
}) {
  const onClose = () => {
    send({ type: 'closeChecklistDialog' })
  }
  if (!dialog) return null
  return createPortal(
    <Modal isOpen={true} onClose={onClose}>
      {match(dialog)
        .with({ mode: 'generation' }, (preview) => (
          <Dialog
            title="Generate tax payment checklist"
            subtitle="Checklist items will be visible to your client in a tax payment section. You can modify them after creation."
            className="w-[960px] max-w-[90vw]"
            buttons={
              <DialogButtons>
                <Button variant="subtle" disabled={preview.status === 'saving'} onClick={onClose}>
                  Cancel
                </Button>
                {preview.status === 'failed' && (
                  <Button onClick={() => send({ type: 'previewDeliveryChecklist' })}>Retry</Button>
                )}
                <Button
                  variant="primary"
                  disabled={preview.status !== 'ready' || preview.items.length === 0}
                  onClick={() => send({ type: 'createDeliveryChecklist' })}
                >
                  {match(preview.status)
                    .with('saving', () => 'Creating…')
                    .otherwise(() => 'Create checklist')}
                </Button>
              </DialogButtons>
            }
          >
            <div className="max-h-[60vh] space-y-4 overflow-y-auto">
              {preview.status === 'loading' && <p role="status">Loading payments…</p>}
              {preview.status === 'saving' && (
                <div role="status" className="flex items-center gap-2">
                  <Spinner className="size-4" />
                  Creating checklist…
                </div>
              )}
              {preview.error && <p role="alert">{preview.error}</p>}
              {preview.status === 'ready' && preview.items.length === 0 && (
                <p>No payments found.</p>
              )}
              {preview.items.length > 0 && (
                <Checklist.Table>
                  {preview.items.map((item, index) => (
                    <ChecklistItemRow key={index} item={item} />
                  ))}
                </Checklist.Table>
              )}
            </div>
          </Dialog>
        ))
        .with({ mode: 'loading' }, () => (
          <Dialog title="Tax payment">
            <div className="p-8" role="status">
              Loading…
            </div>
          </Dialog>
        ))
        .with({ mode: 'edit' }, ({ editor }) =>
          match(editor)
            .with({ kind: 'document_request' }, (editor) => (
              <DocumentRequestDialog
                send={send}
                editor={editor}
                documentTypes={documentTypes}
                reasons={reasons}
                onClose={onClose}
              />
            ))
            .with({ kind: 'questionnaire_entry' }, (editor) => (
              <QuestionnaireEntryDialog
                send={send}
                editor={editor}
                reasons={reasons}
                onClose={onClose}
              />
            ))
            .with({ kind: 'tax_payment' }, (editor) => (
              <TaxPaymentDialog send={send} editor={editor} onClose={onClose} />
            ))
            .exhaustive()
        )
        .with(P.union({ mode: 'attachments' }, { mode: 'complete' }), (dialog) => (
          <ChecklistItemDialog
            send={send}
            subject={checklistItemDialogModel(dialog)}
            isAttachmentPickerOpen={dialog.isAttachmentPickerOpen}
            model={checklistAttachmentModel(sections, dialog.attachments)}
            onClose={onClose}
          />
        ))
        .with({ mode: 'manageCompletion' }, ({ item }) => (
          <ManageChecklistItemCompletion checklistItem={item} onClose={onClose} />
        ))
        .with({ mode: 'delete' }, ({ item }) => (
          <ConfirmDeleteChecklistItemDialog send={send} checklistItem={item} onClose={onClose} />
        ))
        .exhaustive()}
    </Modal>,
    document.body
  )
}

function ConfirmDeleteChecklistItemDialog({
  checklistItem,
  send,
  onClose
}: {
  send: (message: ChecklistMessage) => void
  checklistItem: STChecklistItem
  onClose: () => void
}) {
  return (
    <Dialog
      title={'Confirm delete'}
      className="w-96"
      buttons={
        <DialogButtons>
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => send({ type: 'saveChecklistDialog' })}>
            Delete
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        Are you sure you want to delete the checklist item {formatChecklistItem(checklistItem)}?
      </div>
    </Dialog>
  )
}
